import { Reveal } from "@/components/Reveal";
import { Award, FileText, Globe } from "lucide-react";
import { LEADERSHIP, initialsOf } from "@/data/leaders";

const LeaderCard = ({ leader }) => (
  <article
    data-testid={`leader-card-${leader.id}`}
    className="group h-full rounded-xl border border-[#06102A]/10 bg-white overflow-hidden hover:border-[#06102A]/40 hover:-translate-y-0.5 hover:shadow-[0_20px_40px_-24px_rgba(6,16,42,0.22)] transition-all duration-500"
  >
    <div
      className="relative aspect-square overflow-hidden"
      style={{
        background:
          leader.imageBg ||
          "linear-gradient(160deg, #F1F3F6 0%, #E1E4EB 55%, #C9CDD6 100%)",
      }}
    >
      <div className="absolute inset-0 bg-grid opacity-30 pointer-events-none" />
      {leader.photo ? (
        <img
          src={leader.photo}
          alt={leader.name}
          className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.015] transition-transform duration-700"
          style={{
            objectPosition: leader.imageObjectPosition || "top",
            ...(leader.imageBlend ? { mixBlendMode: leader.imageBlend } : {}),
          }}
          loading="lazy"
        />
      ) : (
        <PlaceholderPortrait initials={initialsOf(leader.name)} />
      )}
      {!leader.imageBg && (
        <div
          className="absolute inset-x-0 bottom-0 h-16 pointer-events-none"
          style={{
            background:
              "linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.7) 100%)",
          }}
        />
      )}
    </div>
    <div className="p-4 md:p-5">
      <h3 className="font-display text-[15px] md:text-base font-medium text-[#06102A] leading-tight">
        {leader.name}
      </h3>
      <p className="mt-1 text-[10px] uppercase tracking-[0.16em] font-semibold text-[#F97316]">
        {leader.role}
      </p>
      <p className="mt-2.5 text-[12.5px] text-[#06102A]/70 leading-relaxed">
        {leader.bio}
      </p>
    </div>
  </article>
);

const PlaceholderPortrait = ({ initials }) => (
  <div
    className="absolute inset-0 grid place-items-center"
    data-testid="leader-placeholder"
  >
    <svg
      className="absolute inset-0 w-full h-full text-[#06102A]/10"
      viewBox="0 0 100 125"
      fill="currentColor"
      aria-hidden="true"
      preserveAspectRatio="xMidYMid slice"
    >
      <circle cx="50" cy="42" r="18" />
      <path d="M50 66 C 28 66, 16 84, 16 110 L 84 110 C 84 84, 72 66, 50 66 Z" />
    </svg>
    <div
      className="relative grid place-items-center h-16 w-16 md:h-20 md:w-20 rounded-full font-display text-lg md:text-xl font-medium text-[#06102A]"
      style={{
        background:
          "radial-gradient(circle at 30% 25%, #FFFFFF 0%, #E5E7EB 55%, #B8BDC8 100%)",
        boxShadow:
          "inset 0 -6px 16px rgba(6,16,42,0.16), inset 0 4px 10px rgba(255,255,255,0.9), 0 12px 28px -14px rgba(6,16,42,0.24)",
      }}
    >
      {initials}
    </div>
  </div>
);

const FACTS = [
  "Three U.S. patents pending",
  "Manufacturing partner: CardLabs",
  "Second manufacturer being brought online",
  "Pilot opportunities across multiple global markets",
  "Blockchain Innovation Award · Asian Banking & Finance 2025",
  "Accenture FinTech Innovation Lab Asia-Pacific finalist",
  "HICOOL Global Entrepreneur Summit semi-finalist",
];

export default function Company() {
  return (
    <main className="bg-white" data-testid="company-page">
      <section className="pt-32 md:pt-40 pb-16 bg-radial-soft relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-50 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 md:px-8 relative">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.22em] font-semibold text-[#06102A]/65">
              Company
            </p>
          </Reveal>
          <Reveal delay={80}>
            <h1 className="mt-6 font-display text-5xl md:text-6xl lg:text-7xl font-medium leading-[1.02] tracking-tight text-[#06102A] max-w-4xl">
              The trust infrastructure for regulated digital finance.
            </h1>
          </Reveal>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-white" data-testid="company-mission">
        <div className="max-w-7xl mx-auto px-6 md:px-8 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
          <Reveal>
            <div>
              <p className="text-xs uppercase tracking-[0.22em] font-semibold text-[#06102A]/65">
                Mission
              </p>
              <h2 className="mt-5 font-display text-4xl md:text-5xl font-medium leading-tight text-[#06102A]">
                To deliver the world's most secure, compliant biometric custody infrastructure.
              </h2>
            </div>
          </Reveal>
          <Reveal delay={120}>
            <div>
              <p className="text-xs uppercase tracking-[0.22em] font-semibold text-[#06102A]/65">
                Vision
              </p>
              <h2 className="mt-5 font-display text-4xl md:text-5xl font-medium leading-tight text-[#06102A]">
                A financial system where digital ownership cannot be stolen, lost, coerced, or
                corrupted.
              </h2>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#FAFBFC]" data-testid="company-leadership">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <p className="text-xs uppercase tracking-[0.22em] font-semibold text-[#06102A]/65">
            Leadership & Track Record
          </p>
          <h2 className="mt-5 font-display text-4xl md:text-5xl font-medium leading-tight text-[#06102A] max-w-7xl">
            Built, regulated, scaled, and exited global payments infrastructure.
          </h2>

          <div className="mt-16" data-testid="leadership-team">
            <Reveal>
              <div className="flex items-end justify-between gap-6 mb-10">
                <div className="max-w-5xl">
                  <p className="text-xs uppercase tracking-[0.22em] font-semibold text-[#06102A]/65">
                    Leadership Team
                  </p>
                  <h3 className="mt-4 font-display text-3xl md:text-4xl font-medium leading-tight text-[#06102A]">
                    Deep expertise across global payments, financial infrastructure, digital identity, cybersecurity, compliance, and institutional fintech.
                  </h3>
                </div>
                <span className="hidden md:block font-mono-vt text-xs text-[#06102A]/45">
                  {LEADERSHIP.length.toString().padStart(2, "0")} executives
                </span>
              </div>
            </Reveal>

            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3.5 md:gap-4">
              {LEADERSHIP.map((l, i) => (
                <Reveal key={l.id} delay={(i % 4) * 70}>
                  <LeaderCard leader={l} />
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-white" data-testid="company-credibility">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <p className="text-xs uppercase tracking-[0.22em] font-semibold text-[#06102A]/65">
            Awards · Patents · Readiness
          </p>
          <h2 className="mt-5 font-display text-4xl md:text-5xl font-medium leading-tight text-[#06102A] max-w-7xl">
            From prototype to production-ready infrastructure.
          </h2>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-3">
            {FACTS.map((f) => (
              <div
                key={f}
                className="flex items-center gap-4 rounded-xl border border-[#06102A]/10 bg-white px-5 py-4"
              >
                {f.toLowerCase().includes("award") || f.toLowerCase().includes("finalist") || f.toLowerCase().includes("semi") ? (
                  <Award className="h-4 w-4 text-[#F97316]" />
                ) : f.toLowerCase().includes("iso") || f.toLowerCase().includes("patent") ? (
                  <FileText className="h-4 w-4 text-[#3B82F6]" />
                ) : (
                  <Globe className="h-4 w-4 text-[#06102A]/60" />
                )}
                <p className="text-sm text-[#06102A]">{f}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
